export type BurnBedsFormFields = {
  burnLicensedAdultBeds: number | null;
  burnLicensedPediatricBeds: number | null;
  burnLicensedTotalBeds: number | null;
  burnStaffedIcuAdultBeds: number | null;
  burnStaffedIcuPediatricBeds: number | null;
  burnStaffedIcuTotalBeds: number | null;
  burnStaffedNonIcuAdultBeds: number | null;
  burnStaffedNonIcuPediatricBeds: number | null;
  burnStaffedNonIcuTotalBeds: number | null;
  burnStaffedTotalAdultBeds: number | null;
  burnStaffedTotalPediatricBeds: number | null;
  burnStaffedTotalBeds: number | null;
  pediatricPatientDefinition: string | null;
};

export const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const addNullable = (a: unknown, b: unknown): number | null => {
  const n1 = toNumberOrNull(a);
  const n2 = toNumberOrNull(b);
  if (n1 == null && n2 == null) return null;
  return (n1 ?? 0) + (n2 ?? 0);
};

export const computeBurnBedTotals = (
  values: Partial<BurnBedsFormFields>,
): Partial<BurnBedsFormFields> => {
  const burnLicensedTotalBeds = addNullable(
    values.burnLicensedAdultBeds,
    values.burnLicensedPediatricBeds,
  );
  const burnStaffedIcuTotalBeds = addNullable(
    values.burnStaffedIcuAdultBeds,
    values.burnStaffedIcuPediatricBeds,
  );
  const burnStaffedNonIcuTotalBeds = addNullable(
    values.burnStaffedNonIcuAdultBeds,
    values.burnStaffedNonIcuPediatricBeds,
  );
  const burnStaffedTotalAdultBeds = addNullable(
    values.burnStaffedIcuAdultBeds,
    values.burnStaffedNonIcuAdultBeds,
  );
  const burnStaffedTotalPediatricBeds = addNullable(
    values.burnStaffedIcuPediatricBeds,
    values.burnStaffedNonIcuPediatricBeds,
  );
  const burnStaffedTotalBeds = addNullable(
    burnStaffedTotalAdultBeds,
    burnStaffedTotalPediatricBeds,
  );

  return {
    burnLicensedTotalBeds,
    burnStaffedIcuTotalBeds,
    burnStaffedNonIcuTotalBeds,
    burnStaffedTotalAdultBeds,
    burnStaffedTotalPediatricBeds,
    burnStaffedTotalBeds,
  };
};

export const formatBedDisplay = (value: number | null | undefined) => (
  value == null ? '' : String(value)
);

export const STAFFED_BURN_BED_FIELD_KEYS = [
  'burnStaffedIcuAdultBeds',
  'burnStaffedNonIcuAdultBeds',
  'burnStaffedIcuPediatricBeds',
  'burnStaffedNonIcuPediatricBeds',
] as const satisfies readonly (keyof BurnBedsFormFields)[];

export type BurnBedsStaffingValidation = {
  isValid: boolean;
  messages: string[];
  fieldErrors: Partial<Record<keyof BurnBedsFormFields, string>>;
};

const validateStaffedAgainstLicensed = (
  categoryLabel: 'adult' | 'pediatric',
  licensed: number | null,
  icu: number | null,
  nonIcu: number | null,
  icuKey: keyof BurnBedsFormFields,
  nonIcuKey: keyof BurnBedsFormFields,
): Pick<BurnBedsStaffingValidation, 'messages' | 'fieldErrors'> => {
  const messages: string[] = [];
  const fieldErrors: Partial<Record<keyof BurnBedsFormFields, string>> = {};

  const licensedLimit = licensed ?? 0;
  const licensedMissing = licensed == null;

  const icuExceedsLicensed = icu != null && icu > licensedLimit;
  const nonIcuExceedsLicensed = nonIcu != null && nonIcu > licensedLimit;

  const staffedExceedsLicensedMsg = (
    field: 'ICU' | 'non-ICU',
  ) => (
    licensedMissing
      ? `Staffed ${field} ${categoryLabel} beds require licensed ${categoryLabel} beds to be entered.`
      : `Staffed ${field} ${categoryLabel} beds cannot exceed licensed ${categoryLabel} beds (${licensed}).`
  );

  if (icuExceedsLicensed) {
    const msg = staffedExceedsLicensedMsg('ICU');
    messages.push(msg);
    fieldErrors[icuKey] = msg;
  }

  if (nonIcuExceedsLicensed) {
    const msg = staffedExceedsLicensedMsg('non-ICU');
    messages.push(msg);
    fieldErrors[nonIcuKey] = msg;
  }

  if (
    icu != null
    && nonIcu != null
    && !icuExceedsLicensed
    && !nonIcuExceedsLicensed
  ) {
    const combined = icu + nonIcu;
    if (combined > licensedLimit) {
      const msg = licensedMissing
        ? `Combined staffed ICU and non-ICU ${categoryLabel} beds require licensed ${categoryLabel} beds to be entered.`
        : `Combined staffed ICU and non-ICU ${categoryLabel} beds (${combined}) cannot exceed licensed ${categoryLabel} beds (${licensed}).`;
      messages.push(msg);
      fieldErrors[icuKey] = msg;
      fieldErrors[nonIcuKey] = msg;
    }
  }

  return { messages, fieldErrors };
};

export const getBurnBedsStaffingValidation = (
  values: Partial<BurnBedsFormFields>,
): BurnBedsStaffingValidation => {
  const adult = validateStaffedAgainstLicensed(
    'adult',
    toNumberOrNull(values.burnLicensedAdultBeds),
    toNumberOrNull(values.burnStaffedIcuAdultBeds),
    toNumberOrNull(values.burnStaffedNonIcuAdultBeds),
    'burnStaffedIcuAdultBeds',
    'burnStaffedNonIcuAdultBeds',
  );

  const pediatric = validateStaffedAgainstLicensed(
    'pediatric',
    toNumberOrNull(values.burnLicensedPediatricBeds),
    toNumberOrNull(values.burnStaffedIcuPediatricBeds),
    toNumberOrNull(values.burnStaffedNonIcuPediatricBeds),
    'burnStaffedIcuPediatricBeds',
    'burnStaffedNonIcuPediatricBeds',
  );

  const messages = [...adult.messages, ...pediatric.messages];
  const fieldErrors = { ...adult.fieldErrors, ...pediatric.fieldErrors };

  return {
    isValid: messages.length === 0,
    messages: [...new Set(messages)],
    fieldErrors,
  };
};
